import ButtonLink from "../components/ButtonLink";
import "./MedicalPage.css";

const bodySystems = [
  {
    label: "Hematology",
    title: (
      <>
        Blood vessels. <strong>Bone marrow.</strong> Sickle cells.
      </>
    ),
    image: "/assets/medical/sickle-cells-1920.webp",
    srcSet:
      "/assets/medical/sickle-cells-960.webp 960w, /assets/medical/sickle-cells-1920.webp 1920w, /assets/medical/sickle-cells-3840.webp 3840w",
    alt: "Healthy and sickled red blood cells moving through a blood vessel",
  },
  {
    label: "Neuroscience",
    title: (
      <>
        Neurons. <strong>Synapses.</strong> Pain pathways.
      </>
    ),
    image: "/assets/medical/neurons-1920.webp",
    srcSet:
      "/assets/medical/neurons-960.webp 960w, /assets/medical/neurons-1920.webp 1920w, /assets/medical/neurons-3840.webp 3840w",
    alt: "A network of neurons carrying illuminated pain signals",
  },
  {
    label: "Gene therapy",
    title: (
      <>
        DNA. CRISPR. <strong>Mechanism of action.</strong>
      </>
    ),
    image: "/assets/medical/crispr-molecule-1920.webp",
    srcSet:
      "/assets/medical/crispr-molecule-960.webp 960w, /assets/medical/crispr-molecule-1920.webp 1920w, /assets/medical/crispr-molecule-3840.webp 3840w",
    alt: "A CRISPR protein editing a strand of DNA at the molecular level",
  },
];

export default function MedicalPage() {
  return (
    <article className="medical-page">
      <section className="medical-hero">
        <img
          alt="Human anatomy with the skeleton and circulatory system visible"
          className="medical-hero__image"
          fetchPriority="high"
          sizes="100vw"
          src="/assets/medical/anatomy-hero-1920.webp"
          srcSet="/assets/medical/anatomy-hero-960.webp 960w, /assets/medical/anatomy-hero-1920.webp 1920w, /assets/medical/anatomy-hero-3840.webp 3840w"
        />
        <div className="medical-hero__wash" />
        <div className="content-width medical-hero__inner">
          <p className="medical-kicker">10k Science for medicine</p>
          <h1>
            Turning <span className="medical-highlight">science data</span>
            <br />
            into science content
          </h1>
          <p className="medical-hero__lead">
            Immersive 3D for medical marketing, scientific communication, and
            biomedical training.
          </p>
          <div className="button-row medical-hero__actions">
            <ButtonLink
              href="https://calendly.com/dynamoid/10k-science-demo"
              target="_blank"
            >
              Talk with our team
            </ButtonLink>
            <ButtonLink
              href="https://viewer.10k.science"
              target="_blank"
              variant="outline"
            >
              See it live
            </ButtonLink>
          </div>
        </div>
      </section>

      <section className="medical-statement">
        <div className="content-width medical-statement__inner">
          <h2>
            From the whole body to the <strong>molecular level.</strong>
          </h2>
          <p>Anatomy. Physiology. Therapeutic mechanisms.</p>
        </div>
      </section>

      <section className="medical-platforms">
        <div className="content-width">
          <header className="medical-platforms__header">
            <p className="medical-kicker">Multi-platform</p>
            <h2>
              One platform. <strong>Three ways to explore.</strong>
            </h2>
          </header>
          <div className="medical-platforms__grid">
            <article className="medical-platform">
              <div className="medical-platform__stage">
                <div className="medical-device medical-device--laptop">
                  <div className="medical-device__screen">
                    <img
                      alt="10k Science web experience showing an interactive patient and AI guide"
                      loading="lazy"
                      sizes="(max-width: 620px) 88vw, 30vw"
                      src="/assets/medical/ai-guide-1600.webp"
                      srcSet="/assets/medical/ai-guide-800.webp 800w, /assets/medical/ai-guide-1600.webp 1600w, /assets/medical/ai-guide-2500.webp 2500w"
                    />
                  </div>
                  <div className="medical-device__base" />
                </div>
              </div>
              <h3>Web</h3>
              <p>Open interactive 3D experiences in any modern browser.</p>
            </article>

            <article className="medical-platform">
              <div className="medical-platform__stage">
                <div className="medical-device medical-device--tablet">
                  <img
                    alt="Molecular science content running on an iOS tablet"
                    loading="lazy"
                    sizes="(max-width: 620px) 82vw, 27vw"
                    src="/assets/medical/platform-ios-1600.webp"
                    srcSet="/assets/medical/platform-ios-800.webp 800w, /assets/medical/platform-ios-1600.webp 1600w, /assets/medical/platform-ios-2560.webp 2560w"
                  />
                </div>
              </div>
              <h3>iOS</h3>
              <p>Touch-first exploration on iPhone and iPad.</p>
            </article>

            <article className="medical-platform">
              <div className="medical-platform__stage medical-platform__stage--vr">
                <img
                  alt="10k Science running with a Meta Quest headset and controllers"
                  className="medical-platform__vr"
                  loading="lazy"
                  sizes="(max-width: 620px) 100vw, 34vw"
                  src="/assets/medical/platform-vr-1600.webp"
                  srcSet="/assets/medical/platform-vr-800.webp 800w, /assets/medical/platform-vr-1600.webp 1600w, /assets/medical/platform-vr-2500.webp 2500w"
                />
              </div>
              <h3>VR</h3>
              <p>Immersive learning on Meta Quest.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="medical-systems" aria-label="Body systems content">
        {bodySystems.map((system) => (
          <figure className="medical-system" key={system.label}>
            <img
              alt={system.alt}
              loading="lazy"
              sizes="(max-width: 620px) 100vw, 88vw"
              src={system.image}
              srcSet={system.srcSet}
            />
            <div className="medical-system__wash" />
            <figcaption>
              <p>{system.label}</p>
              <h2>{system.title}</h2>
            </figcaption>
          </figure>
        ))}
      </section>

      <section className="medical-feature medical-feature--guide">
        <div className="content-width medical-feature__inner">
          <div className="medical-feature__copy">
            <p className="medical-kicker">AI guides</p>
            <h2>
              A guide that <strong>listens.</strong>
            </h2>
            <p>
              Grounded in each experience, guides narrate, ask and answer
              questions by voice, and adapt to every learner in real time.
            </p>
          </div>
          <figure className="medical-feature__media medical-feature__media--viewer">
            <img
              alt="AI-guided CRISPR and sickle cell experience in the 10k Science viewer"
              loading="lazy"
              sizes="(max-width: 900px) 100vw, 62vw"
              src="/assets/medical/ai-guide-1600.webp"
              srcSet="/assets/medical/ai-guide-800.webp 800w, /assets/medical/ai-guide-1600.webp 1600w, /assets/medical/ai-guide-2500.webp 2500w"
            />
          </figure>
        </div>
      </section>

      <section className="medical-feature medical-feature--dashboard">
        <div className="content-width medical-feature__inner medical-feature__inner--reverse">
          <figure className="medical-feature__media medical-feature__media--dashboard">
            <div className="medical-dashboard-shot medical-dashboard-shot--responses">
              <img
                alt="10k Science dashboard showing response distribution and individual learner responses"
                loading="lazy"
                sizes="(max-width: 620px) 100vw, 55vw"
                src="/assets/medical/dashboard-responses-1800.webp"
                srcSet="/assets/medical/dashboard-responses-900.webp 900w, /assets/medical/dashboard-responses-1800.webp 1800w, /assets/medical/dashboard-responses-2800.webp 2800w"
              />
            </div>
            <div className="medical-dashboard-shot medical-dashboard-shot--session">
              <img
                alt="10k Science session dashboard showing a learner answer, AI response, score, and concept summary"
                loading="lazy"
                sizes="(max-width: 620px) 100vw, 42vw"
                src="/assets/medical/dashboard-session-1800.webp"
                srcSet="/assets/medical/dashboard-session-900.webp 900w, /assets/medical/dashboard-session-1800.webp 1800w, /assets/medical/dashboard-session-2800.webp 2800w"
              />
            </div>
          </figure>
          <div className="medical-feature__copy">
            <p className="medical-kicker">Measurable engagement</p>
            <h2>
              See what people <strong>understand.</strong>
            </h2>
            <p>Spoken responses. Concept-level insight.</p>
          </div>
        </div>
      </section>

      <section className="medical-markets">
        <div className="content-width">
          <h2>
            Built for <strong>medicine.</strong>
          </h2>
          <div className="medical-markets__list">
            <article className="medical-market">
              <p className="medical-market__number">01</p>
              <h3>Medical education</h3>
              <p>
                Explore anatomy, physiology, and therapeutic mechanisms from
                the whole body to the molecular level.
              </p>
            </article>
            <article className="medical-market">
              <p className="medical-market__number">02</p>
              <h3>Pharma &amp; biotech</h3>
              <p>
                Communicate complex mechanisms of action through interactive,
                research-grounded 3D content.
              </p>
            </article>
            <article className="medical-market">
              <p className="medical-market__number">03</p>
              <h3>Biomedical training</h3>
              <p>
                Guide learning with adaptive AI and measure understanding
                through spoken-response analytics.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="medical-proof">
        <div className="content-width medical-proof__inner">
          <div>
            <p className="medical-kicker">Research-grounded</p>
            <h2>Built with scientists.</h2>
          </div>
          <div className="medical-proof__logos" aria-label="Funding support">
            <img
              alt="National Institutes of Health"
              loading="lazy"
              src="/assets/original/support/nih.png"
            />
            <img
              alt="National Science Foundation"
              loading="lazy"
              src="/assets/original/images/USNSF_Logo_Lockup_stacked_RGB_reversed_1200ppi--8416bf9d.png"
            />
          </div>
        </div>
      </section>

      <section className="medical-cta">
        <div className="content-width medical-cta__inner">
          <h2>One platform. Every scale of science.</h2>
          <ButtonLink href="https://viewer.10k.science" target="_blank">
            Explore the platform
          </ButtonLink>
        </div>
      </section>
    </article>
  );
}
